import * as React from 'react';
import {
  FC,
  useRef,
  useState,
  useEffect,
  useContext,
  MouseEvent,
} from 'react';
import { style, keyframes } from 'typestyle';
import { IProduct, ISettings, IMedia } from '../../../types';
import { StoreContext } from '../../../services/store';
import useBorderRadius from '../../../hooks/useBorderRadius';
import formatCurrency from '../../../utils/formatCurrency';
import AddToCartButton from '../../Icons/AddToCart/AddToCartButton';
import { mobilePlayerCtaHeightPercent, videoZIndex } from '../../consts';
import { handleAddToCartAction } from '../../../utils/addToCart';
import { renderCartConfirmation } from '../../../utils/renderCartConfirmation';
import Arrow from '../../Icons/Arrows/Arrow';

interface SliderProps {
  products: IProduct[];
  cornerType: ISettings['thumbnail_corners'];
  onContainerClick?: () => void;
  /** Optional post/media override — used in thumbnail mode where context.post is not set */
  post?: IMedia | null;
  /** When provided, renders in thumbnail mode (full-width bar at bottom).
   *  When omitted, renders in player mode (centered floating bar). */
  heightPercentage?: number;
  /** Only used in thumbnail mode — hides the bar when true */
  hidden?: boolean;
}

const Slider: FC<SliderProps> = ({
  products,
  cornerType,
  onContainerClick,
  heightPercentage,
  hidden = false,
  post: postProp,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [confirmedProduct, setConfirmedProduct] = useState<{
    product: IProduct;
    media: IMedia;
  } | null>(null);
  const context = useContext(StoreContext);
  const { config, isMobile } = context;

  const [currentTileIndex, setCurrentTileIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);

  const isSingleProduct = products.length === 1;
  const isThumbnailMode = heightPercentage !== undefined;
  const showArrows = isThumbnailMode && !isSingleProduct && products.length > 1
    && !config.hideArrows;
  const imageSizePx = Math.floor(containerHeight * 0.7);
  const imageBorderRadius = useBorderRadius(imageSizePx, imageSizePx, cornerType);
  const tileBorderRadius = cornerType === 'rounded' ? imageBorderRadius : '0px';

  // Reset index when products change
  useEffect(() => {
    setCurrentTileIndex(0);
  }, [products]);

  const handlePrev = (e: MouseEvent) => {
    e.stopPropagation();
    if (currentTileIndex > 0) {
      setSlideDirection('right');
      setCurrentTileIndex((prev) => prev - 1);
    }
  };

  const handleNext = (e: MouseEvent) => {
    e.stopPropagation();
    if (currentTileIndex < products.length - 1) {
      setSlideDirection('left');
      setCurrentTileIndex((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    const updateSize = () => {
      const height = scrollRef.current?.clientHeight || 0;
      setContainerHeight(height);
    };
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(scrollRef.current);
    updateSize();
    // eslint-disable-next-line consistent-return
    return () => resizeObserver.disconnect();
  }, []);

  const activePost = postProp ?? context.post;

  const handleAddToCart = async (e: MouseEvent, product: IProduct) => {
    e.stopPropagation();
    if (activePost
      && await handleAddToCartAction(config.addToCartHandler, product, activePost)
    ) {
      setConfirmedProduct({ product, media: activePost });
    }
  };

  const handleProductClick = (e: MouseEvent, product: IProduct) => {
    e.stopPropagation();
    if (onContainerClick) {
      onContainerClick();
      return;
    }
    if (activePost) {
      context.triggerEvent(
        'productClicked',
        {
          post: activePost,
          originalEvent: e.nativeEvent,
          product: product.id,
        },
        context.data.id,
      );
      setTimeout(() => {
        window.open(product.link, '_blank');
      }, 300);
    }
  };

  // Shared tile renderer for both desktop carousel and mobile scroll
  const renderProductTile = (product: IProduct) => (
    <div
      key={product.id}
      className={styles.productTile(isSingleProduct, tileBorderRadius, showArrows)}
      onClick={(e) => handleProductClick(e, product)}
      role="button"
      tabIndex={0}
      aria-label={`${product.title}${
        product.price && product.currency
          ? `, ${formatCurrency(product.price, product.currency)}`
          : ''
      }`}
    >
      <div className={styles.imageWrapper}>
        <img
          src={product.image_link}
          alt={product.title}
          className={styles.productImage(imageBorderRadius)}
        />
      </div>
      <div className={styles.productInfo}>
        <span className={styles.productTitle}>{product.title}</span>
        {product.price !== undefined && product.price > 0 && product.currency ? (
          <span className={styles.productPrice}>
            {formatCurrency(product.price, product.currency)}
          </span>
        ) : null}
      </div>
      {config.addToCartHandler ? (
        <div className={styles.addToCartWrapper}>
          <AddToCartButton
            size={isThumbnailMode ? 'big' : 'xlarge'}
            showCircle={false}
            iconColor="white"
            onClick={(e) => { void handleAddToCart(e, product); }}
            ariaLabel={`Add ${product.title} to cart`}
            classPrefix={config.classPrefix}
            customIcon={config.addToCartIcon}
          />
        </div>
      ) : null}
    </div>
  );

  if (!products?.length && !confirmedProduct) return null;

  const finalHeight = isThumbnailMode
    ? `${heightPercentage}%`
    : `${mobilePlayerCtaHeightPercent}%`;

  return (
    <>
      {products.length > 0 ? (
        <div
          className={
            styles.sliderContainer(finalHeight, isThumbnailMode, hidden, isSingleProduct)
          }
        >
          {showArrows ? (
            <div className={styles.carouselWrapper}>
              <div className={styles.carouselArrow}>
                <Arrow
                  direction="left"
                  disabled={currentTileIndex === 0}
                  onClick={handlePrev}
                  show={true}
                  classPrefix={config.classPrefix}
                  size="compact"
                  ariaLabel="Previous product"
                  customArrow={config.arrowLeft}
                />
              </div>
              <div className={styles.slideClip}>
                <div
                  key={currentTileIndex}
                  className={styles.slideAnim(slideDirection)}
                  onAnimationEnd={() => setSlideDirection(null)}
                >
                  {renderProductTile(products[currentTileIndex])}
                </div>
              </div>
              <div className={styles.carouselArrow}>
                <Arrow
                  direction="right"
                  disabled={currentTileIndex === products.length - 1}
                  onClick={handleNext}
                  show={true}
                  classPrefix={config.classPrefix}
                  size="compact"
                  ariaLabel="Next product"
                  customArrow={config.arrowRight}
                />
              </div>
            </div>
          ) : (
            <div
              ref={scrollRef}
              className={styles.scrollArea(isSingleProduct)}
            >
              {products.map((product) => renderProductTile(product))}
            </div>
          )}
        </div>
      ) : null}

      {confirmedProduct ? (
        <div className={styles.confirmationBackdrop} onClick={() => setConfirmedProduct(null)}>
          <div className={styles.confirmationContainer} onClick={(e) => e.stopPropagation()}>
            {renderCartConfirmation(
              confirmedProduct,
              config,
              isMobile,
              cornerType,
              () => setConfirmedProduct(null),
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Slider;

const FONT_FAMILY = "'Fustat', sans-serif";
const PADDING_MULTI = '4px 8px';
const PADDING_SINGLE = '4px 0';
const PADDING_NONE = 0;

function getCtaPadding(isThumbnail: boolean, isSingle: boolean): string | number {
  if (!isThumbnail) return PADDING_NONE;
  return isSingle ? PADDING_SINGLE : PADDING_MULTI;
}

function getTileWidth(isDesktop: boolean | undefined, isSingle: boolean): string | undefined {
  if (isDesktop) return undefined;
  return isSingle ? '92%' : '85%';
}

const slideInFromRight = keyframes({
  '0%': { transform: 'translateX(100%)', opacity: 0 },
  '100%': { transform: 'translateX(0)', opacity: 1 },
});

const slideInFromLeft = keyframes({
  '0%': { transform: 'translateX(-100%)', opacity: 0 },
  '100%': { transform: 'translateX(0)', opacity: 1 },
});

const styles = {
  sliderContainer:
    (height: string, isThumbnail: boolean, hidden: boolean, isSingle: boolean) => style({
      position: 'absolute',
      zIndex: videoZIndex + 1,
      bottom: isThumbnail ? '-4px' : 20,
      left: isThumbnail ? 0 : '50%',
      right: isThumbnail ? 0 : undefined,
      transform: isThumbnail ? undefined : 'translateX(-50%)',
      width: isThumbnail ? '100%' : '90%',
      boxSizing: 'border-box',
      height,
      display: 'flex',
      alignItems: 'center',
      padding: getCtaPadding(isThumbnail, isSingle),
      visibility: hidden ? 'hidden' : 'visible',
      pointerEvents: hidden ? 'none' : 'auto',
    }),

  scrollArea: (isSingle: boolean) => style({
    display: 'flex',
    gap: '8px',
    overflowX: isSingle ? 'hidden' : 'auto',
    overflowY: 'hidden',
    scrollSnapType: isSingle ? 'none' : 'x mandatory',
    scrollBehavior: 'smooth',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: isSingle ? 'center' : 'flex-start',
    $nest: {
      '&::-webkit-scrollbar': { display: 'none' },
    },
  }),

  carouselWrapper: style({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  }),

  carouselArrow: style({
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1,
    minWidth: 24,
  }),

  productTile: (isSingle: boolean, borderRadius: string, isDesktopCarousel?: boolean) => style({
    flex: isDesktopCarousel ? 1 : '0 0 auto',
    minWidth: isDesktopCarousel ? 0 : undefined,
    width: getTileWidth(isDesktopCarousel, isSingle),
    height: '85%',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(20px)',
    '-webkit-backdrop-filter': 'blur(20px)',
    borderRadius,
    cursor: 'pointer',
    scrollSnapAlign: isDesktopCarousel ? undefined : 'center',
    transition: 'background-color 0.2s ease, transform 0.2s ease',
    $nest: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.22)',
      },
      '&:active': {
        transform: 'scale(0.98)',
        backgroundColor: 'rgba(255, 255, 255, 0.28)',
      },
    },
  }),

  imageWrapper: style({
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),

  productImage: (borderRadius: string) => style({
    height: '100%',
    objectFit: 'cover',
    borderRadius,
  }),

  productInfo: style({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '4px',
    minWidth: 0,
    overflow: 'hidden',
  }),

  productTitle: style({
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.2,
    fontFamily: FONT_FAMILY,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '2.4em',
  }),

  productPrice: style({
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: FONT_FAMILY,
  }),

  addToCartWrapper: style({
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),

  confirmationBackdrop: style({
    position: 'absolute',
    zIndex: videoZIndex + 1,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),

  slideClip: style({
    flex: 1,
    overflow: 'hidden',
    height: '100%',
    minWidth: 0,
  }),

  slideAnim: (direction: 'left' | 'right' | null) => style({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    ...(direction ? {
      animationName: direction === 'left' ? slideInFromRight : slideInFromLeft,
      animationDuration: '0.3s',
      animationTimingFunction: 'ease-out',
      animationFillMode: 'forwards',
    } : {}),
  }),

  confirmationContainer: style({
    width: '85%',
    maxHeight: '65vh',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '20px 16px',
    boxSizing: 'border-box',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Fustat', sans-serif",
    color: '#231F20',
  }),
};
